import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './SupplierItemsModal.css';
import { Pagination, PaginationItem } from '@mui/material';
import {
  FaTimes,
  FaDownload,
  FaCaretDown,
  FaSearch,
  FaPills,
  FaSortAmountDown,
  FaSortAmountUp
} from 'react-icons/fa';

/**
 * SupplierItemsModal Component
 * 
 * Displays the catalog of medical and pharmaceutical items supplied by a selected supplier.
 * Matches reference UI from Pic 1:
 * - Header with "Supplier Items" title and close 'X' button
 * - Cyan-blue "Download" action button
 * - Table header with white pill dropdown filters: "Name ▾", "Max Rate", "Min Rate"
 * - Tabular view of items with price bands and stock counts
 * - Material-UI pagination
 * - CSV export capability
 * 
 * @param {boolean} isOpen - Controls visibility of the modal dialog
 * @param {function} onClose - Dismisses the modal
 * @param {object} supplier - The active supplier whose items are being viewed
 */
const SupplierItemsModal = ({ isOpen, onClose, supplier }) => {
  // State for search and filter inputs
  const [searchTerm, setSearchTerm] = useState('');
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const pageSize = 6;

  // Supplier catalog items loaded dynamically from backend API
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch items from backend whenever modal opens for a supplier
  useEffect(() => {
    if (isOpen && supplier?.id) {
      setLoading(true);
      axios
        .get(ENDPOINTS.GET_ALL_SUPPLIER_ITEMS(supplier.id))
        .then((res) => {
          const rawList = Array.isArray(res.data) ? res.data : (res.data?.content || []);
          const mapped = rawList.map((it) => ({
            id: it.id,
            name: it.itemName || it.name,
            category: it.category || 'General',
            unit: it.unit || 'Unit',
            minRate: parseFloat(it.minRate) || 0,
            maxRate: parseFloat(it.maxRate) || 0,
            currentStock: it.currentStock || 0,
            gst: it.gst || '12%'
          }));
          setItems(mapped);
        })
        .catch((err) => {
          console.warn("Could not fetch supplier items from backend", err);
          setItems([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setItems([]);
    }
  }, [isOpen, supplier]);

  /**
   * Filter and sort item records based on user search and rate criteria
   */
  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        // Name search
        if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        let compare = 0;
        if (sortField === 'name') {
          compare = a.name.localeCompare(b.name);
        } else if (sortField === 'minRate') {
          compare = a.minRate - b.minRate;
        } else if (sortField === 'maxRate') {
          compare = a.maxRate - b.maxRate;
        }
        return sortDirection === 'asc' ? compare : -compare;
      });
  }, [items, searchTerm, sortField, sortDirection]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredItems.length / pageSize) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  /**
   * Toggle sort order for a given column field
   * @param {string} field - Column key to sort by
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Exports the filtered supplier items to a CSV spreadsheet
   */
  const handleDownloadCSV = () => {
    const headers = ['ID', 'Item Name', 'Category', 'Max Rate (Rs)', 'Min Rate (Rs)'];
    const rows = filteredItems.map((item) => [
      item.id,
      `"${item.name.replace(/"/g, '""')}"`,
      `"${item.category}"`,
      item.maxRate.toFixed(2),
      item.minRate.toFixed(2)
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    const safeSupplierName = (supplier?.name || 'supplier').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `${safeSupplierName}_items_catalog.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Do not render when closed
  if (!isOpen) return null;

  return (
    <div
      className="supplier-items-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="supplier-items-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Decorative Accent Line (synced with Enterprise card branding) */}
        <div className="supplier-items-accent-bar" />

        {/* ─── Modal Header ────────────────────────────────────────────── */}
        <div className="supplier-items-modal-header">
          <div className="supplier-items-title-group">
            <h3 className="supplier-items-title">Supplier Items</h3>
            {supplier && (
              <span className="supplier-items-subtitle-pill">
                {supplier.name}
              </span>
            )}
          </div>
          <button
            type="button"
            className="supplier-items-close-btn"
            onClick={onClose}
            title="Close dialog"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* ─── Action Bar with Download Button ─────────────────────────── */}
        <div className="supplier-items-action-bar">
          <div className="supplier-items-count-tag">
            Showing <strong>{filteredItems.length}</strong> items
          </div>
          <button
            type="button"
            className="supplier-items-btn-download"
            onClick={handleDownloadCSV}
            title="Export items to CSV"
          >
            <FaDownload /> Download
          </button>
        </div>

        {/* ─── Items Table with Filter Header Row (Matches Pic 1) ──────── */}
        <div className="supplier-items-table-wrapper">
          <table className="supplier-items-table">
            <thead>
              <tr>
                {/* 1. Name Filter Dropdown Header Pill */}
                <th className="th-item-name">
                  <div className="supplier-items-th-pill-wrapper">
                    <button
                      type="button"
                      className={`supplier-items-filter-pill ${searchTerm ? 'has-filter' : ''}`}
                      onClick={() => setShowNameDropdown((prev) => !prev)}
                      title="Filter items by medicine/item name"
                    >
                      <span>Name {searchTerm ? `(${searchTerm})` : ''}</span>
                      <FaCaretDown className="pill-caret" />
                    </button>

                    {showNameDropdown && (
                      <div className="supplier-items-popover" onClick={(e) => e.stopPropagation()}>
                        <div className="popover-heading">Filter Item Name</div>
                        <input
                          type="text"
                          className="popover-search-input"
                          placeholder="Search medicine name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          autoFocus
                        />
                        <div className="popover-actions-row">
                          <button
                            type="button"
                            className="popover-btn-clear"
                            onClick={() => setSearchTerm('')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="popover-btn-apply"
                            onClick={() => setShowNameDropdown(false)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>

                {/* 2. Max Rate Header Pill with Sort */}
                <th className="th-item-max-rate">
                  <div className="supplier-items-th-pill-wrapper">
                    <button
                      type="button"
                      className={`supplier-items-filter-pill ${sortField === 'maxRate' ? 'is-sorted' : ''}`}
                      onClick={() => handleSort('maxRate')}
                      title="Click to sort by Maximum Rate"
                    >
                      <span>Max Rate</span>
                      {sortField === 'maxRate' ? (
                        sortDirection === 'asc' ? <FaSortAmountUp size={11} /> : <FaSortAmountDown size={11} />
                      ) : null}
                    </button>
                  </div>
                </th>

                {/* 3. Min Rate Header Pill with Sort */}
                <th className="th-item-min-rate">
                  <div className="supplier-items-th-pill-wrapper">
                    <button
                      type="button"
                      className={`supplier-items-filter-pill ${sortField === 'minRate' ? 'is-sorted' : ''}`}
                      onClick={() => handleSort('minRate')}
                      title="Click to sort by Minimum Rate"
                    >
                      <span>Min Rate</span>
                      {sortField === 'minRate' ? (
                        sortDirection === 'asc' ? <FaSortAmountUp size={11} /> : <FaSortAmountDown size={11} />
                      ) : null}
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '30px 15px', color: '#64748b' }}>
                    Loading catalog items from database...
                  </td>
                </tr>
              ) : paginatedItems.length > 0 ? (
                paginatedItems.map((item) => (
                  <tr key={item.id} className="supplier-item-row">
                    {/* Item Name & Category */}
                    <td>
                      <div className="item-name-cell">
                        <span className="item-main-name">
                          <FaPills className="item-pill-icon" /> {item.name}
                        </span>
                        <span className="item-category-sub">{item.category}</span>
                      </div>
                    </td>

                    {/* Max Rate */}
                    <td className="item-rate-val">
                      ₹{item.maxRate.toFixed(2)}
                    </td>

                    {/* Min Rate */}
                    <td className="item-rate-val">
                      ₹{item.minRate.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="supplier-items-empty">
                    <FaSearch size={22} style={{ color: '#94a3b8', marginBottom: '8px' }} />
                    <p>No items found matching the selected criteria.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Modal Footer with Pagination ────────────────────────────── */}
        <div className="supplier-items-modal-footer">
          <span className="pagination-summary-text">
            Showing page <strong>{page}</strong> of <strong>{totalPages || 1}</strong> ({filteredItems.length} items)
          </span>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  components={{
                    previous: () => <span>Previous</span>,
                    next: () => <span>Next</span>,
                  }}
                />
              )}
              color="primary"
              variant="outlined"
              shape="rounded"
              size="small"
            />
          )}

          <button
            type="button"
            className="supplier-items-btn-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierItemsModal;
